import React, { useState } from 'react';
import { ArrowRight, Clock, Newspaper, RefreshCw } from 'lucide-react';
import { formatNewsTime } from '../../utils/newsMapper';
import { formatPublishedIst, sourceInitials } from '../../utils/newsDesk';

const Thumbnail = ({ story }) => {
  const [failed, setFailed] = useState(false);
  if (story.image && !failed) {
    return (
      <img
        src={story.image}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover border border-slate-200 bg-slate-100 flex-shrink-0"
      />
    );
  }
  return (
    <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl border border-teal-100 bg-teal-50 text-[#006666] text-sm font-extrabold flex items-center justify-center flex-shrink-0">
      {sourceInitials(story.source)}
    </span>
  );
};

const StoryItem = ({ story, index }) => {
  const published = formatPublishedIst(story.publishedAt);
  const relative = formatNewsTime(story.publishedAt);
  const linkProps = story.url ? { href: story.url, target: '_blank', rel: 'noopener noreferrer' } : null;

  return (
    <li className="flex gap-3 sm:gap-4 px-4 sm:px-5 py-4 sm:py-5 hover:bg-slate-50/60 transition-colors">
      <span className="w-6 pt-1 text-xs font-bold text-slate-300 tabular-nums flex-shrink-0">{String(index + 1).padStart(2, '0')}</span>
      <Thumbnail story={story} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`px-1.5 py-px rounded border text-[10px] font-extrabold uppercase tracking-wide ${story.category.badge}`}>
            {story.category.label}
          </span>
          <span className="text-[11px] font-semibold text-slate-500">{story.source}</span>
          {relative && (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
              <Clock className="w-3 h-3" />
              {relative}
            </span>
          )}
        </div>
        <h3 className="mt-1.5 text-sm sm:text-base font-extrabold text-slate-900 leading-snug">
          {linkProps ? (
            <a {...linkProps} className="hover:text-[#008080]">
              {story.title}
            </a>
          ) : (
            story.title
          )}
        </h3>
        {story.snippet && <p className="mt-1 text-xs sm:text-[13px] text-slate-500 leading-relaxed line-clamp-3">{story.snippet}</p>}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400">{published ? `Published on: ${published}` : ''}</span>
          {linkProps && (
            <a {...linkProps} className="inline-flex items-center gap-1 text-xs font-bold text-[#008080] hover:text-[#006666]">
              Read Full Coverage
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </li>
  );
};

const SkeletonItem = () => (
  <li className="flex gap-4 px-4 sm:px-5 py-5 animate-pulse">
    <span className="w-6" />
    <div className="w-16 h-16 rounded-xl bg-slate-200 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="w-32 h-3 bg-slate-100 rounded" />
      <div className="w-3/4 h-4 bg-slate-200 rounded" />
      <div className="w-full h-3 bg-slate-100 rounded" />
      <div className="w-5/6 h-3 bg-slate-100 rounded" />
    </div>
  </li>
);

export const NewsDeskList = ({
  stories,
  visibleCount,
  onLoadMore,
  headerLabel,
  syncLabel,
  isLoading,
  isRefreshing,
  onRefresh,
  emptyMessage,
  footerNote,
}) => {
  const shown = stories.slice(0, visibleCount);
  const remaining = stories.length - shown.length;

  return (
    <section aria-label="IPO news stories" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/70">
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600">{headerLabel}</p>
        <div className="flex items-center gap-2">
          {syncLabel && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
              {syncLabel}
            </span>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh news"
            title="Refresh news"
            className="w-7 h-7 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-[#008080] disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <ol className="divide-y divide-slate-100">
        {isLoading && Array.from({ length: 5 }).map((_, idx) => <SkeletonItem key={idx} />)}
        {!isLoading && shown.length === 0 && (
          <li className="py-14 text-center px-4">
            <Newspaper className="w-8 h-8 mx-auto text-slate-300" />
            <p className="mt-2 text-sm font-semibold text-slate-700">{emptyMessage}</p>
          </li>
        )}
        {!isLoading && shown.map((story, index) => <StoryItem key={story.id} story={story} index={index} />)}
      </ol>

      {!isLoading && (remaining > 0 || footerNote) && (
        <div className="px-4 sm:px-5 py-4 border-t border-slate-100 text-center space-y-2">
          {remaining > 0 && (
            <button
              type="button"
              onClick={onLoadMore}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-[#008080] hover:text-[#008080] transition-colors"
            >
              Load older updates ({remaining} more)
            </button>
          )}
          {footerNote && <div className="text-[11px] text-slate-400">{footerNote}</div>}
        </div>
      )}
    </section>
  );
};

export default NewsDeskList;
